import React, { useState, useRef, useEffect } from 'react';
import { Calendar, X, Plus } from 'lucide-react';

interface MultiDatePickerProps {
  selectedDates: string[];
  onDatesChange: (dates: string[]) => void;
  placeholder?: string;
  className?: string;
}

const MultiDatePicker: React.FC<MultiDatePickerProps> = ({
  selectedDates,
  onDatesChange,
  placeholder = "Daten auswählen...",
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [hoveredDate, setHoveredDate] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Generate calendar days for current month
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    
    // Convert Sunday=0 to Monday=0 system
    let startingDay = firstDay.getDay();
    startingDay = startingDay === 0 ? 6 : startingDay - 1; // Monday=0, Sunday=6

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }

    return days;
  };

  const formatDate = (date: Date) => {
    // Use local date formatting to avoid timezone issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const toggleDate = (date: Date) => {
    const dateString = formatDate(date);
    const newSelectedDates = selectedDates.includes(dateString)
      ? selectedDates.filter(d => d !== dateString)
      : [...selectedDates, dateString];
    
    onDatesChange(newSelectedDates);
  };

  const removeDate = (dateString: string) => {
    onDatesChange(selectedDates.filter(d => d !== dateString));
  };

  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const getMonthName = (date: Date) => {
    const months = [
      'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
      'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
    ];
    return months[date.getMonth()];
  };

  const days = getDaysInMonth(currentMonth);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Selected dates display */}
      <div 
        className="min-h-[42px] border border-gray-300 rounded-md p-2 bg-white cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
      >
        {selectedDates.length === 0 ? (
          <div className="flex items-center text-gray-500">
            <Calendar className="w-4 h-4 mr-2" />
            <span className="text-sm">{placeholder}</span>
          </div>
        ) : (
          <div className="flex flex-wrap gap-1">
            {selectedDates.map((dateString) => (
              <div
                key={dateString}
                className="flex items-center bg-primary text-white text-xs px-2 py-1 rounded-md"
              >
                <span>{dateString.split('-').reverse().join('.')}</span>
3                <button
                  onClick={() => removeDate(dateString)}
                  className="ml-1 hover:bg-white/20 rounded-full p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="flex items-center bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs px-2 py-1 rounded-md"
            >
              <Plus className="w-3 h-3 mr-1" />
              Datum hinzufügen
            </button>
          </div>
        )}
      </div>

      {/* Calendar dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-50 min-w-[280px]">
          {/* Calendar header */}
          <div className="flex items-center justify-between p-3 border-b border-gray-200">
            <button
              onClick={goToPreviousMonth}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h3 className="font-medium text-gray-900">
              {getMonthName(currentMonth)} {currentMonth.getFullYear()}
            </h3>
            <button
              onClick={goToNextMonth}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Calendar grid */}
          <div className="p-3">
            {/* Day headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((day) => (
                <div key={day} className="text-center text-xs font-medium text-gray-500 py-1">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar days */}
            <div className="grid grid-cols-7 gap-1">
              {days.map((day, index) => (
                <div key={index} className="text-center">
                  {day ? (
                    <button
                      onClick={() => toggleDate(day)}
                      onMouseEnter={() => setHoveredDate(formatDate(day))}
                      onMouseLeave={() => setHoveredDate(null)}
                      className={`
                        w-8 h-8 text-sm rounded-md transition-colors
                        ${selectedDates.includes(formatDate(day))
                          ? 'bg-primary text-white hover:bg-primary/90'
                          : hoveredDate === formatDate(day)
                          ? 'bg-gray-100 text-gray-900'
                          : 'text-gray-900 hover:bg-gray-50'
                        }
                      `}
                    >
                      {day.getDate()}
                    </button>
                  ) : (
                    <div className="w-8 h-8" />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="p-3 border-t border-gray-200 bg-gray-50">
            <div className="flex justify-between items-center text-xs text-gray-600">
              <span>{selectedDates.length} Datum{selectedDates.length !== 1 ? 'e' : ''} ausgewählt</span>
              <button
                onClick={() => onDatesChange([])}
                className="text-red-600 hover:text-red-800 hover:underline"
              >
                Alle löschen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiDatePicker;
